import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from 'helpers/theme';
import { QueryRenderer, graphql } from 'react-relay';
import environment from 'api/environment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCheck, faCopy } from '@fortawesome/pro-regular-svg-icons';
import CoreInput, { InputTypes } from 'sharedComponents/core/Input/CoreInput';
import Button from 'components/Button/Button';
import AvatarUpload from './AvatarUpload';
import { useModalRegistration } from 'views/ModalRegistryProvider';
import headerCheckerboard from 'assets/logo/header-checkerboard.svg';
import { CreateDelegate, UpdateDelegate } from './mutations';
import { DelegateModal_delegate_QueryResponse } from './__generated__/DelegateModal_delegate_Query.graphql';

const useStyles = createUseStyles((theme: Theme) => ({
  '@keyframes overlayIn': {
    from: { opacity: 0 },
    to: { opacity: 1 }
  },
  '@keyframes panelIn': {
    from: { opacity: 0, transform: 'scale(0.94) translateY(24px)' },
    to: { opacity: 1, transform: 'scale(1) translateY(0)' }
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingTop: '7vh',
    background: 'rgba(23, 25, 35, 0.18)',
    animation: '$overlayIn 0.15s ease-out'
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    width: 480,
    maxWidth: 'calc(100vw - 40px)',
    maxHeight: '86vh',
    background: 'white',
    borderRadius: 14,
    border: '1px solid #ededed',
    boxShadow: '0 16px 70px rgba(0, 0, 0, 0.2), 0 3px 12px rgba(0, 0, 0, 0.08)',
    animation: '$panelIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
    overflow: 'hidden'
  },
  cover: {
    position: 'relative',
    flexShrink: 0,
    height: 84,
    background: `
      url(${headerCheckerboard}) left top / auto 100% no-repeat,
      linear-gradient(135deg, #f4f7ff, #eef3ff)
    `,
    borderBottom: '1px solid #ededed'
  },
  closeButton: {
    position: 'absolute',
    top: 14,
    right: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
    height: 30,
    borderRadius: '50%',
    background: 'white',
    border: '1px solid #ededed',
    cursor: 'pointer',
    color: '#74777f',
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor: '#f0f1f3'
    }
  },
  avatarRow: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: -34,
    zIndex: 1
  },
  titleBlock: {
    padding: '12px 24px 18px',
    textAlign: 'center'
  },
  title: {
    fontSize: 17,
    fontWeight: 700,
    color: theme.colors.primaryBlack
  },
  subtitle: {
    marginTop: 4,
    fontSize: 13,
    color: '#74777f'
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 24px 24px'
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#9ea2ad',
    marginBottom: 10
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 16
  },
  nameGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: 14,
    '& > $field': {
      marginBottom: 0
    },
    marginBottom: 16
  },
  label: {
    fontSize: 13,
    fontWeight: 500,
    color: '#74777f',
    marginBottom: 7
  },
  input: {
    boxSizing: 'border-box',
    minHeight: 40,
    padding: '0 14px',
    fontSize: 14,
    color: theme.colors.primaryBlack,
    background: 'white',
    border: '1px solid #ededed',
    borderRadius: 8,
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:focus': {
      borderColor: '#0b57ff',
      boxShadow: '0 0 0 3px rgba(11, 87, 255, 0.1)'
    },
    '&::placeholder': {
      color: '#9ea2ad'
    }
  },
  ttcSection: {
    marginTop: 8
  },
  ttcId: {
    display: 'inline-flex',
    alignItems: 'center',
    height: 40,
    padding: '0 14px',
    fontSize: 14,
    fontWeight: 600,
    color: theme.colors.primaryBlack,
    background: '#f8f9fb',
    border: '1px solid #ededed',
    borderRadius: 8
  },
  toggleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gridGap: 16,
    padding: '14px 16px',
    marginTop: 8,
    background: '#f8f9fb',
    border: '1px solid #ededed',
    borderRadius: 10,
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor: '#f0f1f3'
    }
  },
  toggleLabel: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.colors.primaryBlack
  },
  toggleHint: {
    fontSize: 13,
    color: '#74777f',
    marginTop: 3,
    lineHeight: 1.4
  },
  toggle: {
    position: 'relative',
    flexShrink: 0,
    width: 34,
    height: 20,
    borderRadius: 10,
    background: '#d7dae0',
    transition: 'background-color 0.15s'
  },
  toggleOn: {
    extend: 'toggle',
    background: '#0b57ff'
  },
  toggleKnob: {
    position: 'absolute',
    top: 2,
    left: 2,
    width: 16,
    height: 16,
    borderRadius: '50%',
    background: 'white',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
    transition: 'transform 0.15s ease-out'
  },
  toggleKnobOn: {
    extend: 'toggleKnob',
    transform: 'translateX(14px)'
  },
  errorBanner: {
    padding: '12px 14px',
    marginBottom: 14,
    fontSize: 13,
    color: '#b3261e',
    background: '#fef1f2',
    border: '1px solid #fadcdf',
    borderRadius: 8
  },
  passwordInfo: {
    fontSize: 14,
    color: '#74777f',
    lineHeight: 1.5,
    marginBottom: 16,
    textAlign: 'center'
  },
  passwordRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gridGap: 12,
    padding: '12px 14px',
    background: '#f8f9fb',
    border: '1px solid #ededed',
    borderRadius: 10
  },
  password: {
    fontFamily: 'monospace',
    fontSize: 15,
    fontWeight: 600,
    color: theme.colors.primaryBlack,
    userSelect: 'all',
    wordBreak: 'break-all'
  },
  copyButton: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    gridGap: 6,
    padding: '6px 10px',
    fontSize: 13,
    fontWeight: 500,
    color: '#74777f',
    background: 'white',
    border: '1px solid #ededed',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor: '#f0f1f3'
    }
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gridGap: 10,
    padding: '16px 24px',
    borderTop: '1px solid #ededed'
  }
}));

type DelegateFields = {
  firstName: string;
  lastName: string;
  jobTitle: string;
  email: string;
  phone: string;
  profileImageUrl?: string;
  profileImageUploadToken?: string;
};

const emptyFields: DelegateFields = {
  firstName: '',
  lastName: '',
  jobTitle: '',
  email: '',
  phone: ''
};

type Field = {
  label: string;
  placeholder: string;
  key: keyof DelegateFields;
  type?: InputTypes;
};

const formFields: Field[] = [
  { label: 'First Name', placeholder: 'e.g. John', key: 'firstName' },
  { label: 'Last Name', placeholder: 'e.g. Smith', key: 'lastName' },
  {
    label: 'Job Title',
    placeholder: 'e.g. Screener',
    key: 'jobTitle'
  },
  {
    label: 'Email',
    placeholder: 'e.g. example@fedex.com',
    key: 'email',
    type: 'email'
  },
  {
    label: 'Phone Number',
    placeholder: 'e.g. +44 1234 567890',
    key: 'phone',
    type: 'tel'
  }
];

type Props = {
  isOpen: boolean;
  delegate?: DelegateModal_delegate_QueryResponse['delegate'];
  onClose: () => void;
};

const DelegateModal = ({ isOpen, delegate, onClose }: Props) => {
  const theme = useTheme();
  const classes = useStyles({ theme });
  useModalRegistration(isOpen);

  const isUpdate = delegate !== undefined && delegate !== null;

  const [fields, setFields] = React.useState<DelegateFields>(emptyFields);
  const [sendSetupEmail, setSendSetupEmail] = React.useState(true);
  const [generatedPassword, setGeneratedPassword] = React.useState<
    string | undefined
  >();
  const [copied, setCopied] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>();

  // Reset the form whenever the modal opens or the delegate arrives
  React.useEffect(() => {
    if (!isOpen) return;
    setFields(
      delegate
        ? {
            firstName: delegate.firstName,
            lastName: delegate.lastName,
            jobTitle: delegate.jobTitle,
            email: delegate.email ?? '',
            phone: delegate.telephone ?? '',
            profileImageUrl: delegate.profileImageUrl ?? undefined
          }
        : emptyFields
    );
    setSendSetupEmail(true);
    setGeneratedPassword(undefined);
    setCopied(false);
    setLoading(false);
    setError(undefined);
  }, [isOpen, delegate && delegate.uuid]);

  React.useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (evt: KeyboardEvent) => {
      if (evt.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const setField = (key: keyof DelegateFields) => (value: string) =>
    setFields((f) => ({ ...f, [key]: value }));

  // Limitation: saving always submits the whole form as shown — there is no
  // changed-field tracking, so an update resends unchanged fields too.
  const submit = () => {
    if (loading) return;
    setLoading(true);
    setError(undefined);
    const onError = (err: string) => {
      setError(err);
      setLoading(false);
    };
    if (isUpdate && delegate) {
      UpdateDelegate(
        {
          uuid: delegate.uuid,
          firstName: fields.firstName,
          lastName: fields.lastName,
          jobTitle: fields.jobTitle,
          email: fields.email,
          phone: fields.phone,
          profileImageUploadToken: fields.profileImageUploadToken
        },
        onError,
        () => {
          setLoading(false);
          onClose();
        }
      );
    } else {
      CreateDelegate(
        {
          firstName: fields.firstName,
          lastName: fields.lastName,
          jobTitle: fields.jobTitle,
          email: fields.email,
          phone: fields.phone,
          profileImageUploadToken: fields.profileImageUploadToken
        },
        !sendSetupEmail,
        onError,
        (resp) => {
          setLoading(false);
          const password = resp.createDelegate?.generatedPassword;
          if (password) {
            setGeneratedPassword(password);
          } else {
            onClose();
          }
        }
      );
    }
  };

  const copyPassword = () => {
    if (!generatedPassword) return;
    navigator.clipboard.writeText(generatedPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderField = ({ label, placeholder, key, type }: Field) => (
    <div className={classes.field} key={key}>
      <span className={classes.label}>{label}</span>
      <CoreInput
        type={type ?? 'text'}
        placeholder={placeholder}
        value={fields[key]}
        onChange={setField(key)}
        className={classes.input}
      />
    </div>
  );

  // Only offer the phone field when editing a delegate that already has one
  const visibleFields = formFields.filter(
    (field) =>
      field.key !== 'phone' || (isUpdate && !!(delegate && delegate.telephone))
  );

  const displayName = `${fields.firstName} ${fields.lastName}`.trim();

  const title = generatedPassword
    ? 'Delegate Created'
    : isUpdate
    ? displayName || 'Edit Delegate'
    : 'Add New Delegate';

  const subtitle = generatedPassword
    ? 'Save this password somewhere safe'
    : isUpdate
    ? 'Update their details below'
    : 'Create a profile for a new team member';

  return (
    <div className={classes.overlay} onMouseDown={onClose}>
      <div
        className={classes.panel}
        onMouseDown={(evt) => evt.stopPropagation()}
      >
        <div className={classes.cover}>
          <div className={classes.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </div>
        </div>
        <div className={classes.avatarRow}>
          <AvatarUpload
            name={displayName || 'New Delegate'}
            url={fields.profileImageUrl}
            readonly={!!generatedPassword}
            onUploaded={(token, url) =>
              setFields((f) => ({
                ...f,
                profileImageUploadToken: token,
                profileImageUrl: url
              }))
            }
          />
        </div>
        <div className={classes.titleBlock}>
          <div className={classes.title}>{title}</div>
          <div className={classes.subtitle}>{subtitle}</div>
        </div>
        {generatedPassword ? (
          <div className={classes.body}>
            <p className={classes.passwordInfo}>
              A password was generated for {displayName}. You will not be able
              to see it again, so please take a note of it now.
            </p>
            <div className={classes.passwordRow}>
              <span className={classes.password}>{generatedPassword}</span>
              <div className={classes.copyButton} onClick={copyPassword}>
                <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                {copied ? 'Copied' : 'Copy'}
              </div>
            </div>
          </div>
        ) : (
          <div className={classes.body}>
            {error && (
              <div className={classes.errorBanner}>
                Unable to save delegate: {error}
              </div>
            )}
            <div className={classes.nameGrid}>
              {visibleFields.slice(0, 2).map(renderField)}
            </div>
            {visibleFields.slice(2).map(renderField)}
            {isUpdate && delegate ? (
              <div className={classes.ttcSection}>
                <div className={classes.sectionLabel}>TTC ID</div>
                <span className={classes.ttcId}>{delegate.TTC_ID}</span>
              </div>
            ) : (
              <div
                className={classes.toggleRow}
                onClick={() => setSendSetupEmail((v) => !v)}
              >
                <div>
                  <div className={classes.toggleLabel}>
                    Email the delegate to set their own password
                  </div>
                  <div className={classes.toggleHint}>
                    A link will be sent to the email above. If turned off, a
                    password will be generated for you to share with them.
                  </div>
                </div>
                <div
                  className={sendSetupEmail ? classes.toggleOn : classes.toggle}
                >
                  <div
                    className={
                      sendSetupEmail ? classes.toggleKnobOn : classes.toggleKnob
                    }
                  />
                </div>
              </div>
            )}
          </div>
        )}
        <div className={classes.footer}>
          {generatedPassword ? (
            <Button archetype="flatPrimary" onClick={onClose}>
              Done
            </Button>
          ) : (
            <>
              <Button archetype="flat" onClick={onClose}>
                Cancel
              </Button>
              <Button
                archetype="flatPrimary"
                disabled={loading}
                onClick={submit}
              >
                {loading
                  ? 'Saving…'
                  : isUpdate
                  ? 'Save Changes'
                  : 'Add Delegate'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

type InputProps = {
  isOpen: boolean;
  uuid?: string;
  onClose: () => void;
};

export default (inputProps: InputProps) => {
  if (!inputProps.uuid) {
    return (
      <DelegateModal isOpen={inputProps.isOpen} onClose={inputProps.onClose} />
    );
  }
  return (
    <QueryRenderer
      environment={environment}
      query={graphql`
        query DelegateModal_delegate_Query($uuid: UUID!) {
          delegate(uuid: $uuid) {
            uuid
            firstName
            lastName
            jobTitle
            email
            telephone
            TTC_ID
            profileImageUrl
          }
        }
      `}
      variables={{
        uuid: inputProps.uuid
      }}
      render={({ props }) => {
        const data = props as DelegateModal_delegate_QueryResponse | null;
        return (
          <DelegateModal
            isOpen={inputProps.isOpen}
            onClose={inputProps.onClose}
            delegate={data ? data.delegate : undefined}
          />
        );
      }}
    />
  );
};
